import { isNonNullish, sortBy } from 'remeda';
import { OrderRule } from 'remeda/dist/types/_purryOrderRules';
import { SortingInput } from 'src/graphql/types';

export const sortAlphabetically = <T extends string>(key: T) => {
  return (a: Record<T, string>, b: Record<T, string>) => {
    return a[key].toLowerCase().localeCompare(b[key].toLowerCase());
  };
};

export const sortByDate = <T extends string>(key: T, desc = false) => {
  return (a: Record<T, string>, b: Record<T, string>) => {
    if (desc) {
      return new Date(b[key]).getTime() - new Date(a[key]).getTime();
    }
    return new Date(a[key]).getTime() - new Date(b[key]).getTime();
  };
};

export function sortByInput<T>(
  rawList: T[],
  sortInput?: SortingInput | SortingInput[],
  // optional per-field override: return a comparable value for (item, sortItem)
  resolveValue?: (item: T, sortItem: SortingInput) => unknown,
): T[] {
  const list = [...rawList];
  if (!sortInput) {
    return list;
  }

  const inputs = Array.isArray(sortInput) ? sortInput : [sortInput];

  if (!inputs?.length) {
    return list;
  }

  const selectors = inputs.map((sortItem) => {
    // split on “/” to support nested paths
    const path = sortItem.attribute
      .split('/')
      .map((p) => p.trim())
      .filter(Boolean);

    // a generic “get” for any depth
    const selectorGetter = (item: T) => {
      const resolved = resolveValue?.(item, sortItem);

      if (resolved !== undefined) {
        return resolved;
      }

      const value = path.reduce<unknown>(
        (o, key: string) => (isNonNullish(o) ? o[key as keyof typeof o] : undefined),
        item,
      );

      return isNonNullish(value) ? value : '';
    };

    // descending, use the tuple form; ascending → just the fn
    return sortItem.desc ? ([selectorGetter, 'desc'] as const) : selectorGetter;
  });

  const [firstRule] = selectors as [OrderRule<T>, ...OrderRule<T>[]];

  return sortBy(firstRule)(rawList);
}
